import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, X, Loader2, CalendarIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { BackArrow } from '@/components/BackArrow';
import { AppHeader } from '@/components/layout/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { NotificationRecipientsSection } from '@/components/policies/NotificationRecipientsSection';
import { useClinic } from '@/hooks/useClinic';
import { useClinicStaff } from '@/hooks/useClinicStaff';
import { useClinicCategories } from '@/hooks/useClinicCategories';
import { useAuth } from '@/contexts/AuthContext';
import { createPolicy } from '@/hooks/useCreatePolicy';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface OwnerOption {
  profileId: string;
  name: string;
  email: string;
}


const ROLE_GROUPS = [
  { id: 'all_staff', label: 'All Staff' },
  { id: 'administrative', label: 'Administrative Staff' },
  { id: 'clinical', label: 'Clinical Staff' },
  { id: 'management', label: 'Management' },
];

export default function CreatePolicy() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { data: clinic } = useClinic();
  const { data: staffMembers = [] } = useClinicStaff();
  const { data: clinicCategories = [] } = useClinicCategories();
  const CATEGORIES = clinicCategories.map(c => c.name);
  const { toast } = useToast();

  // Composer integration: pre-fill name and reuse existing policy record
  const composerPolicyName = (location.state as any)?.composerPolicyName || '';
  const composerPolicyId = (location.state as any)?.composerPolicyId || null;
  const composerPdfUrl = (location.state as any)?.composerPdfUrl || null;
  const composerVersionId = (location.state as any)?.composerVersionId || null;
  const composerMode = (location.state as any)?.composerMode || null;
  const isRevision = composerMode === 'update';
  const isFromComposer = !!composerPolicyId;
  
  // Fetch owners from clinic_members with role='owner', mapped to profiles.id
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  
  useEffect(() => {
    async function fetchOwners() {
      if (!profile?.clinic_id) return;
      
      // Fetch clinic_members with role='owner'
      const { data: ownerMembers } = await supabase
        .from('clinic_members')
        .select('id, name, email, role')
        .eq('clinic_id', profile.clinic_id)
        .eq('role', 'owner')
        .order('name');
      
      if (!ownerMembers) return;
      
      // Fetch profiles to get the profile IDs
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('clinic_id', profile.clinic_id);
      
      if (!profiles) return;
      
      // Map clinic_members to profile IDs
      const emailToProfileId = new Map<string, string>();
      profiles.forEach((p) => {
        emailToProfileId.set(p.email.toLowerCase(), p.id);
      });
      
      const mappedOwners = ownerMembers
        .map((m) => ({
          profileId: emailToProfileId.get(m.email.toLowerCase()) ?? '',
          name: m.name,
          email: m.email,
        }))
        .filter((o) => o.profileId !== ''); // Only include owners with matching profiles
      
      setOwners(mappedOwners);
    }
    
    fetchOwners();
  }, [profile?.clinic_id]);
  
  // Current user email for matching
  const currentUserEmail = profile?.email ?? '';

  // Form state
  const [name, setName] = useState(composerPolicyName);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [policyOwner, setPolicyOwner] = useState(profile?.id ?? '');
  const [effectiveDate, setEffectiveDate] = useState<Date>(new Date());
  const [selectedRoleGroups, setSelectedRoleGroups] = useState<string[]>([]);
  const [selectedIndividuals, setSelectedIndividuals] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveType, setSaveType] = useState<'draft' | 'pending_approval' | null>(null);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(undefined);

  // Default next review date to 12 months from effective date
  useEffect(() => {
    if (!nextReviewDate) {
      const d = new Date(effectiveDate);
      d.setMonth(d.getMonth() + 12);
      setNextReviewDate(d);
    }
  }, []);

  // Form validation - different for draft vs submit
  const isBasicFormValid = name.trim() !== '' && category !== '' && (isFromComposer || file !== null);
  
  // Draft: staff assignment optional
  const isDraftFormValid = isBasicFormValid;
  const hasStaffAssignment = selectedRoleGroups.length > 0 || selectedIndividuals.length > 0;
  const isSubmitFormValid = isBasicFormValid && hasStaffAssignment;

  const handleIndividualChange = (profileId: string, checked: boolean) => {
    if (checked) {
      setSelectedIndividuals((prev) => [...prev, profileId]);
    } else {
      setSelectedIndividuals((prev) => prev.filter((id) => id !== profileId));
    }
  };

  const handleRoleGroupChange = (roleId: string, checked: boolean) => {
    if (checked) {
      setSelectedRoleGroups((prev) => [...prev, roleId]);
    } else {
      setSelectedRoleGroups((prev) => prev.filter((id) => id !== roleId));
    }
  };

  const getRoleGroupMembers = (groupId: string) => {
    if (groupId === 'all_staff') return staffMembers;
    if (groupId === 'clinical') return staffMembers.filter((m) => m.role === 'staff');
    if (groupId === 'administrative') return staffMembers.filter((m) => m.role === 'admin');
    if (groupId === 'management') return staffMembers.filter((m) => m.role === 'admin');
    return [];
  };

  const assignmentSummary = useMemo(() => {
    const ids = new Set<string>();
    for (const groupId of selectedRoleGroups) {
      getRoleGroupMembers(groupId).forEach((m) => ids.add(m.id));
    }
    selectedIndividuals.forEach((id) => ids.add(id));
    return { total: ids.size };
  }, [selectedRoleGroups, selectedIndividuals, staffMembers]);

  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleCancel = () => {
    navigate('/policies');
  };

  const handleSave = async (status: 'draft' | 'pending_approval') => {
    if (!profile || !profile.clinic_id || (!file && !isFromComposer)) return;

    setIsSaving(true);
    setSaveType(status);

    try {
      let finalPolicyId: string;

      if (isFromComposer) {
        if (status === 'pending_approval' && !composerPdfUrl) {
          throw new Error('Cannot submit for approval: policy PDF has not been generated yet.');
        }

        // Update the existing composer-created policy record
        const nextReviewDateStr = nextReviewDate
          ? `${nextReviewDate.getFullYear()}-${String(nextReviewDate.getMonth() + 1).padStart(2, '0')}-${String(nextReviewDate.getDate()).padStart(2, '0')}`
          : undefined;

        const { error: updateErr } = await supabase
          .from('policies')
          .update({
            name,
            category,
            description: description || null,
            policy_owner: policyOwner || profile.id,
            effective_date: format(effectiveDate, 'yyyy-MM-dd'),
            next_review_date: nextReviewDateStr,
            review_cadence_months: 12,
          })
          .eq('id', composerPolicyId);

        if (updateErr) throw new Error('Failed to update policy');

        // Handle staff assignments for composer policy
        const assignmentRows: { policy_id: string; user_id: string; assigned_by: string; assignment_type: string; assigned_role: string | null }[] = [];
        for (const groupId of selectedRoleGroups) {
          const members = getRoleGroupMembers(groupId);
          for (const m of members) {
            assignmentRows.push({
              policy_id: composerPolicyId,
              user_id: m.id,
              assigned_by: profile.id,
              assignment_type: 'role_group',
              assigned_role: groupId,
            });
          }
        }
        for (const userId of selectedIndividuals) {
          assignmentRows.push({
            policy_id: composerPolicyId,
            user_id: userId,
            assigned_by: profile.id,
            assignment_type: 'individual',
            assigned_role: null,
          });
        }
        if (assignmentRows.length > 0) {
          const { error: assignErr } = await supabase
            .from('policy_assignments')
            .insert(assignmentRows);
          if (assignErr) console.error('Assignment insert error:', assignErr);
        }

        // Insert or update policy_versions row via atomic SECURITY DEFINER RPC.
        // Server-side: validates policy ownership, locks policy row, computes
        // next version_number, sets version_status='pending_approval' server-side,
        // derives created_by from auth.uid(), writes audit log atomically.
        // Replaces the direct client INSERT/UPDATE that was broken by S30 Task 1
        // dropping the policy_versions INSERT RLS. Errors now THROW (not just log)
        // so the user sees a real failure instead of an inconsistent state.
        if (composerPdfUrl) {
          const { data: versionData, error: versionErr } = await supabase.rpc(
            'upsert_composer_policy_version',
            {
              p_policy_id:  composerPolicyId,
              p_pdf_url:    composerPdfUrl,
              p_version_id: composerVersionId,
            }
          );

          if (versionErr) {
            console.error('Version upsert error:', versionErr);
            throw new Error(`Failed to create policy version: ${versionErr.message}`);
          }

          if (status === 'pending_approval') {
            const { error: submitErr } = await supabase.rpc(
              'submit_policy_version_for_review',
              {
                p_policy_id:  composerPolicyId,
                p_version_id: (versionData as any)?.version_id ?? composerVersionId,
              }
            );
            if (submitErr) {
              console.error('Submit for review error:', submitErr);
              throw new Error(`Failed to submit for approval: ${submitErr.message}`);
            }
          }
        }

        finalPolicyId = composerPolicyId;
      } else {
        // Existing createPolicy flow
        finalPolicyId = await createPolicy({
          name,
          category,
          description: description || undefined,
          policyOwner: policyOwner || profile.id,
          reviewCadenceMonths: 12,
          effectiveDate: format(effectiveDate, 'yyyy-MM-dd'),
          nextReviewDate: nextReviewDate
            ? `${nextReviewDate.getFullYear()}-${String(nextReviewDate.getMonth() + 1).padStart(2, '0')}-${String(nextReviewDate.getDate()).padStart(2, '0')}`
            : undefined,
          roleGroups: selectedRoleGroups,
          individualStaffIds: selectedIndividuals,
          file: file!,
          versionNotes: versionNotes || undefined,
          status,
        });
      }

      // Save custom recipients
      if (selectedRecipients.length > 0) {
        const recipientRows = selectedRecipients.map((userId) => ({
          policy_id: finalPolicyId,
          user_id: userId,
          added_by: profile.id,
        }));
        const { error: recipientError } = await supabase
          .from('policy_recipients')
          .insert(recipientRows);
        if (recipientError) {
          console.error('Recipient insert error:', recipientError);
        }
      }

      // Trigger approval notification immediately when submitting for approval
      if (status === 'pending_approval') {
        supabase.functions.invoke('trigger-approval-notification').catch(err =>
          console.error('Failed to trigger approval notifications:', err)
        );
      }

      toast({
        title: status === 'draft' ? 'Draft saved' : 'Submitted for approval',
        description: isFromComposer ? 'Policy updated successfully.' : 'Policy created successfully.',
      });

      navigate(`/policies/${finalPolicyId}`);
    } catch (error) {
      console.error('Create policy error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create policy',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
      setSaveType(null);
    }
  };

  const handleSaveAsDraft = () => handleSave('draft');
  const handleSubmitForApproval = () => handleSave('pending_approval');

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Navigation Header */}
      <AppHeader />

      {/* Page Title */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center mb-6">
          <BackArrow to="/policies" />
          <h1 className="text-[28px] font-extrabold tracking-tight">Create New Policy</h1>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-4 space-y-6">
        {/* Section 1: Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Policy Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter policy name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-destructive">*</span>
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter policy description (optional)"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Ownership & Review */}
        <Card>
          <CardHeader>
            <CardTitle>Ownership & Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="owner">Assign To</Label>
              <Select value={policyOwner} onValueChange={setPolicyOwner}>
                <SelectTrigger id="owner">
                  <SelectValue placeholder="Select who to assign this policy to" />
                </SelectTrigger>
                <SelectContent>
                  {owners.map((owner) => {
                    const isCurrentUser = owner.email.toLowerCase() === currentUserEmail.toLowerCase();
                    return (
                      <SelectItem key={owner.profileId} value={owner.profileId}>
                        {owner.name}{isCurrentUser ? ' (You)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(effectiveDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={effectiveDate}
                    onSelect={(d) => d && setEffectiveDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Next Review Date - editable calendar picker */}
            <div className="space-y-2">
              <Label>Next Review Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !nextReviewDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {nextReviewDate ? format(nextReviewDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextReviewDate}
                    onSelect={setNextReviewDate}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Defaults to 12 months from effective date. Pick any date to override.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Staff Assignment */}
        <Card>
          <CardHeader>
            <CardTitle>Staff Assignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Role Groups */}
            <div className="space-y-2">
              <Label>
                Role Groups <span className="text-muted-foreground text-xs font-normal">(required before approval)</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ROLE_GROUPS.map((role) => {
                    const members = getRoleGroupMembers(role.id);
                    const isExpanded = expandedGroups.includes(role.id);
                    return (
                      <Collapsible key={role.id} open={isExpanded} onOpenChange={(open) => {
                        setExpandedGroups(prev => open ? [...prev, role.id] : prev.filter(g => g !== role.id));
                      }}>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={role.id}
                            checked={selectedRoleGroups.includes(role.id)}
                            onCheckedChange={(checked) =>
                              handleRoleGroupChange(role.id, checked as boolean)
                            }
                          />
                          <CollapsibleTrigger asChild>
                            <button type="button" className="flex items-center gap-1.5 text-sm font-normal cursor-pointer hover:text-foreground text-foreground/90 transition-colors">
                              {role.label} ({members.length})
                              {members.length > 0 && (
                                isExpanded
                                  ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                                  : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                              )}
                            </button>
                          </CollapsibleTrigger>
                        </div>
                        {members.length > 0 && (
                          <CollapsibleContent className="ml-6 mt-1.5 mb-1 rounded-md bg-muted/50 px-3 py-2 overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                            <p className="font-semibold text-xs text-foreground mb-1.5">Members</p>
                            <ul className="space-y-1">
                              {members.map((m) => (
                                <li key={m.id} className="flex items-center gap-2 text-sm leading-relaxed">
                                  <span className="text-muted-foreground">•</span>
                                  <span>{m.name}</span>
                                  <span className="text-xs text-muted-foreground">({m.role})</span>
                                </li>
                              ))}
                            </ul>
                          </CollapsibleContent>
                        )}
                      </Collapsible>
                    );
                  })}
              </div>
              <p className="text-sm text-muted-foreground">
                Optional for drafts — can assign staff later before approval
              </p>
            </div>

            {/* Individual Staff */}
            <div className="space-y-2">
              <Label>Individual Staff</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {staffMembers.map((member) => (
                  <div key={member.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`staff-${member.id}`}
                      checked={selectedIndividuals.includes(member.id)}
                      onCheckedChange={(checked) =>
                        handleIndividualChange(member.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={`staff-${member.id}`} className="font-normal cursor-pointer text-sm">
                      {member.name} <span className="text-muted-foreground">({member.role})</span>
                    </Label>
                  </div>
                ))}
                {staffMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground italic col-span-2">No staff members found</p>
                )}
              </div>
            </div>

            {/* Assignment Summary */}
            {(selectedRoleGroups.length > 0 || selectedIndividuals.length > 0) && (
              <div className="border rounded-md p-4 bg-muted/30 space-y-2">
                <p className="text-sm font-medium">Policy will be assigned to:</p>
                {selectedRoleGroups.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedRoleGroups.map((groupId) => {
                      const group = ROLE_GROUPS.find((r) => r.id === groupId);
                      const count = getRoleGroupMembers(groupId).length;
                      return (
                        <Badge key={groupId} variant="secondary" className="text-xs">
                          {group?.label} ({count})
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {selectedIndividuals.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedIndividuals.map((id) => {
                      const member = staffMembers.find((m) => m.id === id);
                      return (
                        <Badge key={id} variant="outline" className="text-xs">
                          {member?.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground pt-1">
                  Total unique people: <span className="font-semibold text-foreground">{assignmentSummary.total}</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Policy Document — hidden when coming from Composer */}
        {!isFromComposer && (
          <Card>
            <CardHeader>
              <CardTitle>Policy Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>
                  Document <span className="text-destructive">*</span>
                </Label>
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {!file ? (
                  <label
                    htmlFor="file-upload"
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                      "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                      isDragOver ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                    )}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">
                      Click or drag to upload policy document
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">
                      PDF only, max 100MB · In Word? File → Export → PDF
                    </span>
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        <Upload className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveFile}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="versionNotes">Version Notes</Label>
                <Textarea
                  id="versionNotes"
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  placeholder="Add notes about this version (optional)"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 5: Notifications */}
        <NotificationRecipientsSection
          policyOwner={policyOwner || profile?.id || ''}
          selectedRecipients={selectedRecipients}
          onRecipientsChange={setSelectedRecipients}
          status="draft"
        />
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSaveAsDraft}
                disabled={!isDraftFormValid || isSaving}
                className="hover:bg-muted hover:shadow-sm"
              >
                {isSaving && saveType === 'draft' && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save as Draft
              </Button>
              <Button
                onClick={handleSubmitForApproval}
                disabled={!isSubmitFormValid || isSaving}
                title={!isSubmitFormValid && isBasicFormValid && !hasStaffAssignment ? 'Please assign at least one staff group or individual before submitting for approval' : undefined}
              >
                {isSaving && saveType === 'pending_approval' && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit for Approval
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
